import { ChangeDetectionStrategy, Component, computed, signal, viewChild } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { FormComponent } from '@modules/forms/components/form/form.component';
import { FormOutputs } from '@modules/forms/models/form-outputs';
import { provideDressfit } from '@pixacare/dressfit-angular';
import { TuiDialogContext, TuiHint } from '@taiga-ui/core';
import { TuiBadge, TuiSegmented } from '@taiga-ui/kit';
import { injectContext } from '@taiga-ui/polymorpheus';
import { toProtocolReports } from '../../utils/to-protocol-reports';
import { SadmEntityProtocolsComponent } from '../sadm-entity-protocols/sadm-entity-protocols.component';
import { SadmDialogFormInput } from './sadm-dialog-form-input';
import { SadmDialogFormOutput } from './sadm-dialog-form-output';

const ANALYSIS_FORM_INDEX = 0;
const PROTOCOL_FORM_INDEX = 1;

@Component({
  selector: 'pxc-sadm-dialog-form',
  templateUrl: './sadm-dialog-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormComponent, TranslocoDirective, TuiSegmented, TuiBadge, SadmEntityProtocolsComponent, TuiHint],
  providers: [provideDressfit()],
  // One fixed height for the whole dialog: every tab fills it, so switching tabs never resizes
  // the dialog.
  host: { class: 'flex h-[75vh] min-h-0 flex-col gap-2' },
})
export class SadmDialogFormComponent {
  private readonly context = injectContext<TuiDialogContext<SadmDialogFormOutput, SadmDialogFormInput>>();

  readonly analysis = this.context.data.analysis;
  readonly protocol = this.context.data.protocol;
  readonly protocolsHistory = this.context.data.protocolsHistory ?? [];
  readonly moduleInputs = this.context.data.formInputs;
  readonly readOnly = this.context.data.readOnly ?? false;
  readonly isAnalysisReadOnly = this.analysis.readOnly || this.readOnly;
  readonly isProtocolReadOnly = this.protocol?.readOnly || this.readOnly;

  // A read-only protocol is shown as the entity's protocols history rather than as a form nobody
  // can fill in.
  readonly protocolReports = toProtocolReports(this.protocolsHistory, this.protocol?.form);

  // With nothing to fill and no history to read, the protocol side has nothing to show at all: no
  // tab, and nothing holding the dialog back from completing on the analysis alone.
  readonly hasProtocolTab = !!this.protocol && (!this.isProtocolReadOnly || this.protocolReports.length > 0);

  readonly activeFormIndex = signal(ANALYSIS_FORM_INDEX);

  // The forms stay mounted, so the content of the panel that becomes visible slides in: from the right when going
  // forward, from the left when going back (its navigation buttons stay in place). Nothing slides until the user first
  // switches.
  readonly slideDirection = signal<-1 | 0 | 1>(0);

  // The protocol form mounts on its first visit and then stays mounted: unmounting it would
  // drop its own FormBridge state, and mounting it upfront would run its AUTO modules before
  // the user ever switches to it.
  readonly isProtocolMounted = signal(false);

  private readonly analysisOutputs = signal<FormOutputs | null>(null);
  private readonly protocolOutputs = signal<FormOutputs | null>(null);

  readonly isAnalysisFormActive = computed(() => this.activeFormIndex() === ANALYSIS_FORM_INDEX);
  readonly isProtocolFormActive = computed(() => this.activeFormIndex() === PROTOCOL_FORM_INDEX);

  private readonly protocolForm = viewChild<FormComponent>('protocolForm');
  private readonly analysisForm = viewChild<FormComponent>('analysisForm');

  readonly showProtocolInfoBadge = computed(
    () => !this.isProtocolReadOnly && this.protocolsHistory.length === 0 && !this.protocolForm()?.formState()?.isDirty,
  );
  readonly showProtocolErrorBadge = computed(
    () => !this.isProtocolReadOnly && this.protocolForm()?.formState()?.isValid === false,
  );
  readonly showAnalysisErrorBadge = computed(
    () => !this.isAnalysisReadOnly && this.analysisForm()?.formState()?.isValid === false,
  );

  readonly finishText = signal('Terminer');

  showAnalysis(): void {
    this.activateForm(ANALYSIS_FORM_INDEX);
  }

  showProtocol(): void {
    this.activateForm(PROTOCOL_FORM_INDEX);
    this.isProtocolMounted.set(true);
  }

  onAnalysisCompleted(formOutputs: FormOutputs | null): void {
    if (!formOutputs) {
      this.cancel();
      return;
    }

    this.analysisOutputs.set(formOutputs);
    this.completeWhenEveryFormIsFilled();
  }

  onProtocolCompleted(formOutputs: FormOutputs | null): void {
    if (!formOutputs) {
      this.cancel();
      return;
    }

    this.protocolOutputs.set(formOutputs);
    this.completeWhenEveryFormIsFilled();
  }

  closeProtocolHistory(): void {
    this.completeWhenEveryFormIsFilled();
  }

  private completeWhenEveryFormIsFilled(): void {
    const analysis = this.analysisOutputs() ?? (this.isAnalysisReadOnly ? this.analysis.formAnswer : null);
    if (!analysis) {
      this.showAnalysis();
      return;
    }

    const protocol = this.protocolOutputs();
    if (this.hasProtocolTab && !this.isProtocolReadOnly && !protocol) {
      this.showProtocol();
      return;
    }

    this.context.completeWith(protocol ? { analysis, protocol } : { analysis });
  }

  private activateForm(index: number): void {
    this.slideDirection.set(Math.sign(index - this.activeFormIndex()) as -1 | 0 | 1);
    this.activeFormIndex.set(index);
  }

  private cancel(): void {
    this.context.completeWith(null);
  }
}
